import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { ContratoElectricidadViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-contrato-electricidad-autocomplete",
    templateUrl: "./contrato-electricidad-autocomplete.component.html",
    styleUrls: ["./contrato-electricidad-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ContratoElectricidadAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class ContratoElectricidadAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Contrato de Electricidad";
	@Input() placeholder =
		"Buscar contrato de electricidad por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint =
		"Busque por código o descripción de contrato de electricidad";

	// Outputs
	@Output() contratoElectricidadSelected =
		new EventEmitter<ContratoElectricidadViewModel>();

	// State
	contratosElectricidad: ContratoElectricidadViewModel[] = [];
	contratoElectricidadId: number | null = null;
	selectedContratoElectricidad: ContratoElectricidadViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadContratosElectricidad();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.contratoElectricidadId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de contratos de electricidad
	 */
	private loadContratosElectricidad(): void {
		this.masterService
			.getContratoElectricidadList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (contratosElectricidad) => {
					this.contratosElectricidad = contratosElectricidad;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los contratos de electricidad",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedContratoElectricidad =
			this.contratoElectricidadId != null
				? (this.contratosElectricidad.find(
						(c) => c.id === this.contratoElectricidadId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un contrato de electricidad
	 */
	displayContratoElectricidad = (
		contratoElectricidad: ContratoElectricidadViewModel | string,
	): string => {
		if (typeof contratoElectricidad === "string") {
			return contratoElectricidad;
		}
		return contratoElectricidad &&
			contratoElectricidad.codigo &&
			contratoElectricidad.descripcion
			? `${contratoElectricidad.codigo} - ${contratoElectricidad.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(
		contratoElectricidad: ContratoElectricidadViewModel | null,
	): void {
		this.contratoElectricidadId = contratoElectricidad
			? contratoElectricidad.id
			: null;
		this.selectedContratoElectricidad = contratoElectricidad;
		this.onChange(this.contratoElectricidadId);
		this.onTouched();
		if (contratoElectricidad) {
			this.contratoElectricidadSelected.emit(contratoElectricidad);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
